import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { T, useTranslation } from "../context/TranslationContext";
import { Newspaper, ArrowRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Newsletter() {
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useTranslation();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/newsletter/issues");
        if (alive) setIssues(res.data.issues || []);
      } catch (err) {
        if (alive) setError(formatApiError(err.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const latest = issues[0];
  const archive = issues.slice(1);

  return (
    <section className="bg-pearl min-h-[calc(100vh-5rem)] py-16" data-testid="newsletter-page">
      <div className="section-container">
        {/* Masthead */}
        <header className="text-center border-b-4 border-double border-navy pb-6 mb-12" data-testid="newsletter-masthead">
          <p className="eyebrow text-saffron">
            <T>Volume I · The Broadsheet</T>
          </p>
          <h1
            className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl text-navy tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            <T>The Ambedkarite Times</T>
          </h1>
          <p className="mt-3 text-sm uppercase tracking-[0.35em] text-navy/60">
            <T>News, Reflection & Dhamma · From the Society to the World</T>
          </p>
          <p className="mt-2 text-xs italic text-navy/50">
            <T>Currently reading in</T>: <span className="font-medium text-navy/80">{lang.toUpperCase()}</span>
          </p>
        </header>

        {loading && (
          <div className="text-center py-20 text-navy/60" data-testid="newsletter-loading">
            <T>Loading issues…</T>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-20 text-red-700" data-testid="newsletter-error">
            {error}
          </div>
        )}

        {!loading && !error && issues.length === 0 && (
          <div className="bg-white border border-navy/10 p-12 text-center" data-testid="newsletter-empty">
            <Newspaper size={36} className="mx-auto text-saffron mb-4" />
            <h2 className="font-display text-3xl text-navy">
              <T>The presses are warming up</T>
            </h2>
            <p className="mt-3 text-navy/70 max-w-xl mx-auto">
              <T>
                The first edition of our digital broadsheet is being typeset. Subscribe by joining as a Community
                Member to receive each new issue in your inbox.
              </T>
            </p>
            <Link
              to="/membership"
              className="mt-6 inline-block px-6 py-3 bg-navy text-pearl text-sm tracking-wide hover:bg-navy-light"
              data-testid="newsletter-empty-join-cta"
            >
              <T>Join free as Community Member</T>
            </Link>
          </div>
        )}

        {!loading && !error && latest && (
          <article
            className="bg-white border border-navy/10 p-8 sm:p-12 mb-16 shadow-sm"
            data-testid="newsletter-latest-issue"
          >
            <div className="flex items-center justify-between border-b border-navy/15 pb-3 mb-6 text-xs uppercase tracking-[0.25em] text-navy/60">
              <span>{latest.edition_label}</span>
              <span>{formatDate(latest.issue_date)}</span>
            </div>
            <p className="eyebrow text-saffron">
              <T>Latest Edition</T>
            </p>
            <h2
              className="mt-2 font-display text-4xl sm:text-5xl text-navy"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              <T>{latest.title}</T>
            </h2>
            {latest.editorial_lead && (
              <p className="mt-4 text-lg italic text-navy/80 leading-relaxed">
                <T>{latest.editorial_lead}</T>
              </p>
            )}
            {latest.lead_headline && (
              <p className="mt-4 text-sm text-navy/70">
                <T>Lead story</T>: <span className="font-medium text-navy">{latest.lead_headline}</span>
              </p>
            )}
            <div className="mt-6 text-xs uppercase tracking-[0.25em] text-navy/50">
              {latest.article_count} <T>articles in this issue</T>
            </div>
            <Link
              to={`/newsletter/${latest.slug}`}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-navy text-pearl text-sm tracking-wide hover:bg-navy-light"
              data-testid="newsletter-read-latest"
            >
              <T>Read this edition</T> <ArrowRight size={16} />
            </Link>
          </article>
        )}

        {archive.length > 0 && (
          <div data-testid="newsletter-archive">
            <h3 className="font-display text-2xl text-navy mb-6 border-b border-navy/15 pb-2">
              <T>Archive</T>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {archive.map((iss) => (
                <Link
                  key={iss.issue_id}
                  to={`/newsletter/${iss.slug}`}
                  data-testid={`newsletter-archive-${iss.issue_id}`}
                  className="bg-white border border-navy/10 p-6 hover:border-saffron transition-colors block"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-navy/60">
                    <span>{iss.edition_label}</span>
                    <span>{formatDate(iss.issue_date)}</span>
                  </div>
                  <h4
                    className="mt-3 font-display text-xl text-navy"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    <T>{iss.title}</T>
                  </h4>
                  {iss.lead_headline && (
                    <p className="mt-2 text-sm text-navy/70 italic">
                      <T>{iss.lead_headline}</T>
                    </p>
                  )}
                  <div className="mt-4 text-xs text-saffron uppercase tracking-widest">
                    <T>Read</T> →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
